import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  LayoutGrid, Users, Store as StoreIcon, BadgeCheck, ShieldHalf, ShoppingBag, Package, ClipboardList,
  Star, LayoutList, Truck, Bike, Settings2, CreditCard, HandCoins, Banknote, QrCode, Send, Undo2, Handshake, Trash2, Plus,
  Percent, Crown, Megaphone, Flag, Scale, MessageSquareWarning, LifeBuoy, BarChart3, KeyRound, ScrollText, Settings, Gift,
} from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';
import api, { formatApiError } from '../../lib/api';
import { fileUrl } from '../../lib/upload';
import { Switch } from '../../components/ui/switch';
import { KpiCard } from '../../components/commerce/KpiCard';
import { StatusBadge } from '../../components/commerce/StatusBadge';
import { EmptyState } from '../../components/commerce/EmptyState';
import { fmtRM2, timeAgo, PLANS } from '../../data/mockData';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Stub, ArrowUpIcon, ArrowDownIcon } from './common';

export function NotificationsCenter() {
  const { notifications, markRead, markAllRead } = useApp();
  const [tab, setTab] = useState('All');
  const tabs = ['All', 'Critical', 'Merchants', 'Orders', 'Delivery', 'Finance', 'Trust', 'System'];
  const items = notifications.filter((n) => n.recipientType === 'admin').filter((n) => {
    if (tab === 'All') return true;
    if (tab === 'Critical') return n.priority === 'CRITICAL';
    return n.category.toLowerCase().startsWith(tab.toLowerCase().replace(/s$/, ''));
  });
  return (
    <div data-testid="admin-notifications">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-base font-semibold text-kelate-ink">Notification Center</h2>
        <button data-testid="admin-mark-all-read" onClick={() => markAllRead('admin')} className="text-xs font-medium text-kelate-wood">Mark all read</button>
      </div>
      <div className="mb-4 flex gap-1 overflow-x-auto border-b border-kelate-line">
        {tabs.map((t) => (
          <button key={t} data-testid={`notif-tab-${t.toLowerCase()}`} onClick={() => setTab(t)} className={`px-3 py-2 text-sm ${tab === t ? 'border-b-2 border-kelate-darkwood font-semibold text-kelate-darkwood' : 'text-kelate-soft'}`}>{t}</button>
        ))}
      </div>
      <div className="space-y-2">
        {items.length === 0 && <EmptyState icon={LayoutList} title="All clear" description="No notifications in this category." />}
        {items.map((n) => (
          <button key={n.id} data-testid={`admin-notif-${n.id}`} onClick={() => { markRead(n.id); }}
            className={`flex w-full items-start gap-3 rounded-md border bg-kelate-surface px-4 py-3 text-left ${n.priority === 'CRITICAL' ? 'border-kelate-red/40' : 'border-kelate-line'} ${n.isRead ? 'opacity-60' : ''}`}>
            <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${n.priority === 'CRITICAL' ? 'bg-kelate-red' : n.priority === 'HIGH' ? 'bg-kelate-wood' : 'bg-kelate-line'}`} />
            <span className="flex-1">
              <span className="text-sm font-medium text-kelate-ink">{n.title}</span>
              <span className="block text-xs text-kelate-soft">{n.message}</span>
              <span className="mt-1 block text-[10px] uppercase tracking-wide text-kelate-soft">{n.priority} · {n.category} · {timeAgo(n.createdAt)}</span>
            </span>
            {n.actionUrl && <Link to={n.actionUrl} data-testid={`notif-action-${n.id}`} className="rounded-sm border border-kelate-darkwood px-2.5 py-1 text-[11px] font-medium text-kelate-darkwood">Review</Link>}
          </button>
        ))}
      </div>
    </div>
  );
}

export function AuditLog() {
  const { auditLog } = useApp();
  return (
    <div data-testid="admin-audit-log">
      <h2 className="mb-4 text-base font-semibold text-kelate-ink">Audit Log</h2>
      <div className="overflow-x-auto rounded-3xl bg-kelate-surface shadow-[0_1px_4px_rgba(81,68,55,0.08)]">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-kelate-line text-left text-xs uppercase tracking-wider text-kelate-soft">
            <th className="px-4 py-3">Admin</th><th className="px-4 py-3">Action</th><th className="px-4 py-3">Entity</th><th className="px-4 py-3">Change</th><th className="px-4 py-3">Time</th><th className="px-4 py-3">IP</th>
          </tr></thead>
          <tbody>
            {auditLog.map((a) => (
              <tr key={a.id} className="border-b border-kelate-line/60 last:border-0" data-testid={`audit-${a.id}`}>
                <td className="px-4 py-2.5 text-kelate-ink">{a.admin}</td>
                <td className="px-4 py-2.5 text-kelate-ink">{a.action}</td>
                <td className="px-4 py-2.5 font-mono text-xs">{a.entity}</td>
                <td className="px-4 py-2.5 text-xs text-kelate-soft">{a.prev} → {a.next}</td>
                <td className="px-4 py-2.5 text-xs text-kelate-soft">{timeAgo(a.at)}</td>
                <td className="px-4 py-2.5 text-xs text-kelate-soft">{a.ip}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function Analytics() {
  const { orders, customProducts } = useApp();
  const gmv = orders.reduce((s, o) => s + o.itemSubtotal, 0);
  const revenue = orders.reduce((s, o) => s + o.commissionAmount + o.deliveryFee, 0);
  const consumers = new Set(orders.map((o) => o.customerPhone)).size;
  const days = [];
  for (let i = 13; i >= 0; i--) {
    const d = new Date();
    d.setDate(d.getDate() - i);
    const key = d.toISOString().slice(0, 10);
    const dayOrders = orders.filter((o) => (o.createdAt || '').slice(0, 10) === key);
    days.push({
      day: d.toLocaleDateString('en-MY', { day: 'numeric', month: 'short' }),
      GMV: Math.round(dayOrders.reduce((s, o) => s + o.itemSubtotal, 0) * 100) / 100,
      Revenue: Math.round(dayOrders.reduce((s, o) => s + o.commissionAmount + o.deliveryFee, 0) * 100) / 100,
    });
  }
  const byMerchant = {};
  orders.forEach((o) => { byMerchant[o.merchantName] = (byMerchant[o.merchantName] || 0) + o.itemSubtotal; });
  const topMerchants = Object.entries(byMerchant).map(([name, g]) => ({ name, GMV: Math.round(g * 100) / 100 })).sort((a, b) => b.GMV - a.GMV).slice(0, 5);
  const byDistrict = {};
  orders.forEach((o) => { const d = o.address?.district || 'Unknown'; byDistrict[d] = (byDistrict[d] || 0) + 1; });
  const totalOrders = orders.length || 1;
  const districts = Object.entries(byDistrict).map(([d, c]) => [d, Math.round((c / totalOrders) * 100)]).sort((a, b) => b[1] - a[1]).slice(0, 6);
  const topCats = [...customProducts].sort((a, b) => b.sold - a.sold).slice(0, 5);
  const noData = orders.length === 0;
  return (
    <div data-testid="admin-analytics" className="space-y-6">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <KpiCard label="Revenue" value={fmtRM2(revenue)} testId="an-revenue" />
        <KpiCard label="GMV" value={fmtRM2(gmv)} testId="an-gmv" />
        <KpiCard label="Orders" value={orders.length} testId="an-orders" />
        <KpiCard label="Consumers" value={consumers} testId="an-consumers" />
      </div>
      {noData && <p className="rounded-2xl bg-secondary/60 px-4 py-3 text-xs text-kelate-soft" data-testid="analytics-empty">Charts will populate as real orders come in.</p>}
      <section className="rounded-3xl bg-kelate-surface shadow-[0_1px_4px_rgba(81,68,55,0.08)] p-4" data-testid="an-gmv-chart">
        <h3 className="mb-4 text-sm font-semibold text-kelate-ink">GMV & Revenue — last 14 days</h3>
        <div className="h-56">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={days} margin={{ top: 4, right: 8, left: -18, bottom: 0 }}>
              <XAxis dataKey="day" tick={{ fontSize: 10, fill: '#8B7E6A' }} tickLine={false} axisLine={false} interval={2} />
              <YAxis tick={{ fontSize: 10, fill: '#8B7E6A' }} tickLine={false} axisLine={false} />
              <Tooltip formatter={(v) => fmtRM2(v)} contentStyle={{ borderRadius: 12, border: '1px solid #E3DCCF', fontSize: 12 }} />
              <Area type="monotone" dataKey="GMV" stroke="#514437" fill="#514437" fillOpacity={0.12} strokeWidth={2} />
              <Area type="monotone" dataKey="Revenue" stroke="#7D9165" fill="#7D9165" fillOpacity={0.15} strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </section>
      <div className="grid gap-6 lg:grid-cols-2">
        <section className="rounded-3xl bg-kelate-surface shadow-[0_1px_4px_rgba(81,68,55,0.08)] p-4" data-testid="an-top-merchants">
          <h3 className="mb-4 text-sm font-semibold text-kelate-ink">Top Merchants by GMV</h3>
          {topMerchants.length === 0 ? <p className="text-xs text-kelate-soft">No sales yet.</p> : (
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={topMerchants} layout="vertical" margin={{ top: 0, right: 8, left: 8, bottom: 0 }}>
                  <XAxis type="number" hide />
                  <YAxis type="category" dataKey="name" width={110} tick={{ fontSize: 11, fill: '#514437' }} tickLine={false} axisLine={false} />
                  <Tooltip formatter={(v) => fmtRM2(v)} contentStyle={{ borderRadius: 12, border: '1px solid #E3DCCF', fontSize: 12 }} />
                  <Bar dataKey="GMV" fill="#A9803E" radius={[0, 6, 6, 0]} barSize={16} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </section>
        <section className="rounded-3xl bg-kelate-surface shadow-[0_1px_4px_rgba(81,68,55,0.08)] p-4">
          <h3 className="mb-4 text-sm font-semibold text-kelate-ink">District Distribution</h3>
          {districts.length === 0 ? <p className="text-xs text-kelate-soft">No orders yet.</p> : (
            <div className="space-y-2.5">
              {districts.map(([d, pct]) => (
                <div key={d} className="flex items-center gap-3 text-sm" data-testid={`district-stat-${d.toLowerCase().replace(/\s+/g, '-')}`}>
                  <span className="w-28 text-kelate-ink">{d}</span>
                  <div className="h-2 flex-1 overflow-hidden rounded-full bg-secondary"><div className="h-full rounded-full bg-kelate-wood" style={{ width: `${pct}%` }} /></div>
                  <span className="w-10 text-right text-xs text-kelate-soft">{pct}%</span>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
      <section className="rounded-3xl bg-kelate-surface shadow-[0_1px_4px_rgba(81,68,55,0.08)] p-4">
        <h3 className="mb-4 text-sm font-semibold text-kelate-ink">Top Products</h3>
        {topCats.length === 0 ? <p className="text-xs text-kelate-soft">No products yet.</p> : (
          <div className="space-y-2.5">
            {topCats.map((p) => (
              <div key={p.id} className="flex items-center gap-3 text-sm">
                {p.image ? (
                  <img src={fileUrl(p.image)} alt="" className="h-8 w-8 rounded-sm border border-kelate-line object-cover" />
                ) : (
                  <span className="flex h-8 w-8 items-center justify-center rounded-sm border border-kelate-line bg-secondary text-[10px] font-semibold text-kelate-soft">{p.name[0]}</span>
                )}
                <span className="flex-1 truncate text-kelate-ink">{p.name}</span>
                <span className="text-xs text-kelate-soft">{p.sold} sold</span>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

